import React, {createContext, useCallback, useContext, useMemo, useState, useEffect} from 'react';
import {RSVP} from '../../rsvp-backend/src/types/interfaces'
import RSVPService from '../services/rsvpApiService';
import {flags} from '../flags';

type Ctx = {
    rsvp: RSVP | null;
    setRsvp: (r: RSVP | null) => void;
    sendCode: (code: string) => Promise<void>;
    isLoading: boolean;
    error: string | null;
};

const RsvpContext = createContext<Ctx | undefined>(undefined);

export const useRsvp = () => {
    const ctx = useContext(RsvpContext);
    if (!ctx) throw new Error('useRsvp must be used within RsvpProvider');
    return ctx;
};

export const RsvpProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [rsvp, setRsvp] = useState<RSVP | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (flags.useRsvpSessionStorage) {
            const storedRsvp = sessionStorage.getItem('rsvp');
            if (storedRsvp) {
                setRsvp(JSON.parse(storedRsvp));
            }
        }
        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (flags.useRsvpSessionStorage) {
            if (rsvp) {
                sessionStorage.setItem('rsvp', JSON.stringify(rsvp));
            } else {
                sessionStorage.removeItem('rsvp');
            }
        }
    }, [rsvp]);

    const sendCode = useCallback(async (code: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const rsvpData = await RSVPService.getRSVPByCode(code);
            setRsvp(rsvpData);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
            console.error("Failed to send code", errorMessage);
            setError(errorMessage);
            setRsvp(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const value = useMemo(() => ({rsvp, setRsvp, sendCode, isLoading, error}), [rsvp, sendCode, isLoading, error]);

    return <RsvpContext.Provider value={value}>{children}</RsvpContext.Provider>;
};